import { useEffect, useState } from 'react';
import Drawer from '~/components/Drawer';
import {
    fetchWebhookErrorDetail,
    replayWebhookError,
    updateWebhookError,
} from '~/services/webhookErrors';
import type {
    WebhookErrorDetail as WebhookErrorDetailT,
    WebhookErrorStatus,
} from '~/types/webhookError';
import { queryClient } from '~/main';
import { WEBHOOK_ERROR_UNREAD_COUNT_KEY } from '~/hooks/useWebhookErrorUnreadCount';

interface Props {
    id: number;
    onClose: () => void;
    onChanged?: () => void;
}

const STATUS_OPTIONS: { value: WebhookErrorStatus; label: string }[] = [
    { value: 'new', label: 'New' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'resolved', label: 'Resolved' },
];

const RECORD_LINK: Record<string, (id: number) => string | null> = {
    campaign_request: (id) => `/campaign-requests/${id}`,
    // donation/event currently have no admin detail route; fall back to text label.
};

const Section = ({
    title,
    children,
    defaultOpen = false,
}: {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className='border-t py-3'>
            <button
                className='w-full text-left font-medium flex justify-between items-center'
                onClick={() => setOpen(!open)}
            >
                <span>{title}</span>
                <span>{open ? '−' : '+'}</span>
            </button>
            {open && <div className='mt-2'>{children}</div>}
        </div>
    );
};

const PrettyJson = ({ value }: { value: unknown }) => {
    const text = JSON.stringify(value, null, 2);
    return (
        <div className='relative'>
            <button
                className='absolute top-1 right-1 text-xs px-2 py-0.5 border rounded bg-white'
                onClick={() => navigator.clipboard.writeText(text)}
            >
                Copy
            </button>
            <pre className='bg-gray-50 border rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap'>
                {text}
            </pre>
        </div>
    );
};

export default function WebhookErrorDetail({ id, onClose, onChanged }: Props) {
    const [detail, setDetail] = useState<WebhookErrorDetailT | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<WebhookErrorStatus | ''>('');
    const [pendingNotes, setPendingNotes] = useState('');
    const [showReplayConfirm, setShowReplayConfirm] = useState(false);
    const [replayMessage, setReplayMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
        null
    );

    useEffect(() => {
        let cancelled = false;
        setError(null);
        fetchWebhookErrorDetail(id)
            .then((d) => {
                if (!cancelled) {
                    setDetail(d);
                    setPendingStatus(d.status);
                    setPendingNotes(d.resolution_notes ?? '');
                }
            })
            .catch((e) => {
                if (!cancelled) setError(e.message ?? 'Failed to load');
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    const saveStatus = async () => {
        if (!detail) return;
        setBusy(true);
        try {
            const updated = await updateWebhookError(id, {
                status: pendingStatus || undefined,
                resolution_notes: pendingNotes,
            });
            setDetail(updated);
            onChanged?.();
            queryClient.invalidateQueries({ queryKey: WEBHOOK_ERROR_UNREAD_COUNT_KEY });
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const doReplay = async () => {
        if (!detail) return;
        setShowReplayConfirm(false);
        setBusy(true);
        setReplayMessage(null);
        try {
            const result = await replayWebhookError(id, true);
            setDetail(result.webhook_error);
            setPendingStatus(result.webhook_error.status);
            setPendingNotes(result.webhook_error.resolution_notes ?? '');
            setReplayMessage({
                kind: result.success ? 'ok' : 'err',
                text: result.success
                    ? `Replay succeeded (id=${result.webhook_error.resulting_object_id ?? '?'})`
                    : `Replay failed: ${result.error ?? 'unknown error'}`,
            });
            onChanged?.();
            queryClient.invalidateQueries({ queryKey: WEBHOOK_ERROR_UNREAD_COUNT_KEY });
        } catch (e) {
            setReplayMessage({ kind: 'err', text: (e as Error).message });
        } finally {
            setBusy(false);
        }
    };

    const drawerWidth = 'w-full md:w-[640px]';
    const setIsOpen = (next: boolean) => {
        if (!next) onClose();
    };

    if (error) {
        return (
            <Drawer isOpen setIsOpen={setIsOpen} onClose={onClose} width={drawerWidth} title='Webhook error'>
                <div className='text-red-600'>{error}</div>
            </Drawer>
        );
    }
    if (!detail) {
        return (
            <Drawer isOpen setIsOpen={setIsOpen} onClose={onClose} width={drawerWidth} title='Webhook error'>
                <div>Loading…</div>
            </Drawer>
        );
    }

    const recordHref = RECORD_LINK[detail.resulting_object_type]?.(detail.resulting_object_id ?? 0);

    return (
        <Drawer
            isOpen
            setIsOpen={setIsOpen}
            onClose={onClose}
            width={drawerWidth}
            title={`${detail.source} · ${detail.http_status} · #${detail.id}`}
        >
            <div className='text-sm text-gray-600 mb-4'>
                Created: {new Date(detail.created_at).toLocaleString()}
                {detail.acknowledged_at && (
                    <>
                        {' · '}
                        Acknowledged by {detail.acknowledged_by_email ?? 'someone'} at{' '}
                        {new Date(detail.acknowledged_at).toLocaleString()}
                    </>
                )}
                {detail.resolved_at && (
                    <>
                        {' · '}
                        Resolved by {detail.resolved_by_email ?? 'someone'} at{' '}
                        {new Date(detail.resolved_at).toLocaleString()}
                    </>
                )}
            </div>

            {/* Status controls */}
            <div className='border rounded p-3 mb-4'>
                <label className='block text-sm font-medium mb-1'>Status</label>
                <select
                    className='border rounded px-3 py-2 w-full mb-2'
                    value={pendingStatus}
                    onChange={(e) => setPendingStatus(e.target.value as WebhookErrorStatus)}
                >
                    {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <label className='block text-sm font-medium mb-1'>Resolution notes</label>
                <textarea
                    className='border rounded px-3 py-2 w-full mb-2'
                    rows={3}
                    value={pendingNotes}
                    onChange={(e) => setPendingNotes(e.target.value)}
                />
                <button
                    className='px-3 py-1 bg-primary text-white rounded disabled:opacity-50'
                    onClick={saveStatus}
                    disabled={busy}
                >
                    Save
                </button>
            </div>

            {/* Replay */}
            <div className='mb-4'>
                <button
                    className='px-4 py-2 bg-primary text-white rounded disabled:opacity-50'
                    onClick={() => setShowReplayConfirm(true)}
                    disabled={busy}
                >
                    Replay payload
                </button>
                {replayMessage && (
                    <div
                        className={`mt-2 text-sm ${
                            replayMessage.kind === 'ok' ? 'text-green-700' : 'text-red-700'
                        }`}
                    >
                        {replayMessage.text}
                    </div>
                )}
                {detail.last_replayed_at && (
                    <div className='text-xs text-gray-500 mt-1'>
                        Last replayed {new Date(detail.last_replayed_at).toLocaleString()} ·{' '}
                        {detail.last_replay_succeeded ? '✓' : '✗'} · {detail.replay_count} attempt
                        {detail.replay_count === 1 ? '' : 's'}
                    </div>
                )}
            </div>

            {/* Created record link */}
            {detail.resulting_object_id && (
                <div className='mb-4'>
                    {recordHref ? (
                        <a className='text-blue-600 underline' href={recordHref}>
                            View created record ({detail.resulting_object_type} #
                            {detail.resulting_object_id})
                        </a>
                    ) : (
                        <span className='text-gray-700'>
                            Created record: {detail.resulting_object_type} #
                            {detail.resulting_object_id}
                        </span>
                    )}
                </div>
            )}

            {/* Sections */}
            <Section title='Error' defaultOpen>
                <div className='text-sm mb-2'>{detail.error_message}</div>
                {detail.exception_type && (
                    <div className='text-xs text-gray-500 mb-2'>
                        Exception type: {detail.exception_type}
                    </div>
                )}
                {detail.source_frame && (
                    <div className='text-xs text-gray-700 mb-2 font-mono break-all'>
                        <span className='text-gray-500'>Source: </span>
                        {detail.source_frame.file}:{detail.source_frame.line}
                        <span className='text-gray-500'> in {detail.source_frame.function}</span>
                    </div>
                )}
                {detail.error_detail !== null && detail.error_detail !== undefined && (
                    <PrettyJson value={detail.error_detail} />
                )}
            </Section>
            <Section title='Payload'>
                <PrettyJson value={detail.payload} />
            </Section>
            <Section title='Headers'>
                <table className='w-full text-xs'>
                    <tbody>
                        {Object.entries(detail.headers ?? {}).map(([k, v]) => (
                            <tr key={k} className='border-b'>
                                <td className='py-1 pr-2 font-mono whitespace-nowrap'>{k}</td>
                                <td className='py-1 break-all'>{v}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>
            {detail.traceback && (
                <Section title='Traceback'>
                    <pre className='bg-gray-50 border rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap'>
                        {detail.traceback}
                    </pre>
                </Section>
            )}

            {/* Replay confirm modal */}
            {showReplayConfirm && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]'>
                    <div className='bg-white rounded p-6 max-w-lg w-full'>
                        <h3 className='font-semibold text-lg mb-2'>Confirm replay</h3>
                        <p className='text-sm mb-3'>
                            Re-fire the original payload through the {detail.source} handler? On
                            success, this error will be auto-resolved.
                        </p>
                        <div className='max-h-64 overflow-y-auto mb-4'>
                            <PrettyJson value={detail.payload} />
                        </div>
                        <div className='flex justify-end gap-2'>
                            <button
                                className='px-3 py-1 border rounded'
                                onClick={() => setShowReplayConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className='px-3 py-1 bg-primary text-white rounded'
                                onClick={doReplay}
                            >
                                Replay
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Drawer>
    );
}
